<script setup lang="ts">
interface Props {
	title?: string
	icon?: string
	iconColor?: string
	/** Lift + tinted shadow + primary border shift on hover */
	hoverable?: boolean
	/** Clickable-card affordance: pointer, press feedback, focus ring */
	interactive?: boolean
	/** Fade/slide in on mount (use for dashboard-style card grids) */
	entrance?: boolean
	/** @deprecated cards are flat + bordered by default now; kept for API compatibility */
	flat?: boolean
}

withDefaults(defineProps<Props>(), {
	title: undefined,
	icon: undefined,
	iconColor: undefined,
	hoverable: false,
	interactive: false,
	entrance: false,
	flat: false,
})
</script>

<template>
	<v-card
		elevation="0"
		class="base-card"
		:class="{
			'base-card--hoverable': hoverable,
			'base-card--interactive': interactive,
			'base-card--entrance': entrance,
		}"
		rounded="lg"
	>
		<v-card-title
			v-if="title || $slots.header"
			class="base-card__header d-flex align-center"
		>
			<slot name="header">
				<i
					v-if="icon"
					:class="icon"
					class="base-card__icon mr-3"
					:style="iconColor ? { color: iconColor } : undefined"
				/>
				<span class="text-subtitle-1 font-weight-medium">{{ title }}</span>
			</slot>
			<v-spacer />
			<slot name="header-actions" />
		</v-card-title>

		<v-card-text>
			<slot />
		</v-card-text>

		<v-card-actions v-if="$slots.actions">
			<slot name="actions" />
		</v-card-actions>
	</v-card>
</template>

<style scoped lang="scss">
.base-card {
	transition:
		transform 250ms $ease-standard,
		box-shadow 250ms $ease-standard,
		border-color 250ms $ease-standard;

	&--hoverable:hover {
		transform: translateY(-2px);
		box-shadow: $shadow-card-hover !important;
		border-color: rgba(var(--v-theme-primary), 0.25) !important;
	}

	&--interactive {
		cursor: pointer;

		&:active {
			transform: scale(0.99);
		}

		&:focus-visible {
			outline: 2px solid $focus-ring-color;
			outline-offset: 2px;
		}
	}

	&--entrance {
		animation: card-enter 300ms $ease-out-expo backwards;
	}

	&__header {
		padding-bottom: 8px;
	}

	&__icon {
		font-size: 18px;
	}
}
</style>
